import dayjs from 'dayjs';
import { get } from 'lodash';
import capitalize from 'lodash/capitalize';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DATE_RANGE_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { InvestorDashboardBalanceChart } from '@/modules/investor-dashboard/components/InvestorDashboardBalanceChart.tsx';
import InfoDetailsSet from '@/modules/issuer-dashboard/components/InfoDetailsSet.tsx';
import IssuedCapitalChart from '@/modules/issuer-dashboard/components/IssuedCapitalChart.tsx';
import ShareholderContactDetails from '@/modules/issuer-dashboard/components/ShareholderContactDetails.tsx';
import { getKycFallbackMessage } from '@/modules/issuer-dashboard/components/holder-tabs/kycFallbackMessage.ts';
import { mapDateRangeToObj } from '@/modules/issuer-dashboard/helpers/mapDateRangeToObj.ts';
import {
  useShareholderBalanceStatisticsQuery,
  useShareholderChartBalanceQuery,
} from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { AnimatedNumbersSet, InfoCard, Spinner } from 'ui';
import {
  BronIDVerificationStatusResponse,
  IssuerBalancesListV1SrIssuerBalancesGetParams,
  ShareholderExtendResponse,
} from 'api';

interface OverviewTabProps {
  isKycDoneOutsideBronId: boolean;
  kycData?: BronIDVerificationStatusResponse;
  kycLoading: boolean;
  shareholder?: ShareholderExtendResponse | null;
}

const OverviewTab = ({
  kycData,
  kycLoading,
  shareholder,
  isKycDoneOutsideBronId,
}: OverviewTabProps) => {
  const [params, setParams] = useSearchParams();
  const date_range = params.get(DATE_RANGE_SEARCH_PARAM_KEY) ?? 'all';
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

  const shareholderRegisterId = shareholder?.register_id?.toString() || '';

  const { data: statistics, isLoading: isStatisticLoading } = useShareholderBalanceStatisticsQuery(
    shareholderRegisterId,
    { entity_id: shareholder?.entity_id || 0 },
    !shareholder?.entity_id,
  );

  const { isLoading: isBalanceLoading, data: balance } = useShareholderChartBalanceQuery(
    {
      ...mapDateRangeToObj(date_range || ''),
      entity_id: shareholder?.entity_id || 0,
    } as IssuerBalancesListV1SrIssuerBalancesGetParams,
    Number(registerId),
    !shareholder?.entity_id,
  );

  const statisticsData = useMemo(
    () => [
      { title: 'Portfolio value', number: statistics?.portfolio_value || 0, prefix: '$' },
      { title: 'Performance', number: statistics?.performance || 0, prefix: '+', suffix: '%' },
      { title: 'Number of investments', number: statistics?.number_of_investments || 0 },
      { title: 'Dividends received', number: statistics?.dividends_received || 0, prefix: '$' },
    ],
    [statistics],
  );

  const handleChangeDateRange = (date_range: string) => {
    params.set(DATE_RANGE_SEARCH_PARAM_KEY, date_range);
    setParams(params, { replace: false });
  };

  const detailsData = useMemo(() => {
    const res = [
      { label: 'Entity type', value: capitalize(shareholder?.entity.type) },
      {
        label: 'Country',
        value: countryOptions.find(({ value }) => value === shareholder?.country)?.label,
      },
    ];

    if ('abn' in (shareholder?.entity_details?.investor_details || {})) {
      res.push({
        label: 'ABN',
        value: get(shareholder, 'entity_details.investor_details.abn'),
      });
    }

    res.push({
      label: 'Registration address',
      value: shareholder?.registration_address,
    });

    if ('business_name_trustee' in (shareholder?.entity_details?.investor_details || {})) {
      res.push({
        label: 'Business name trustee',
        value: get(shareholder, 'entity_details.investor_details.business_name_trustee'),
      });
    }

    return res;
  }, [shareholder]);

  const kycDetailsData = useMemo(
    () => [
      // { label: 'Bron Link', value: 'mock' },
      { label: 'Risk Rating', value: kycData?.idvRiskScore?.description },
      { label: 'Name', value: kycData?.name },
      {
        label: 'Created date',
        value: dayjs(+(kycData?.timestamp || '0') * 1000).format('YYYY-MM-DD, HH:mm'),
      },
      // { label: 'On Watch List', value: 'mock' },
    ],
    [kycData],
  );

  if (!shareholder)
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  return (
    <div className="flex flex-col gap-6">
      <AnimatedNumbersSet numbers={statisticsData} loading={isStatisticLoading} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <InfoCard title="Details">
          <InfoDetailsSet set={detailsData} />
        </InfoCard>
        <InfoCard title="KYC details">
          {isKycDoneOutsideBronId ? (
            <div className="flex items-center justify-center h-24">
              <p className="font-semibold text-center">{getKycFallbackMessage(shareholder)}</p>
            </div>
          ) : (
            <InfoDetailsSet loading={kycLoading} set={kycDetailsData} />
          )}
        </InfoCard>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ShareholderContactDetails shareholder={shareholder} />
        <IssuedCapitalChart title="Holdings" queryOptions={{ entity_id: shareholder.entity_id }} />
      </div>
      <InvestorDashboardBalanceChart
        handleChangeDateRange={handleChangeDateRange}
        date_range={date_range || ''}
        balances={balance?.balances}
        current_balance={statistics?.portfolio_value}
        isLoading={isBalanceLoading}
      />
    </div>
  );
};

export default OverviewTab;
